import {
  Component,
  inject,
  input,
  output,
  Injector,
  afterNextRender,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';

import moment from 'moment-timezone';

import type { AutomatedActionDueDate } from '@core/automated-actions-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { getDefaultTimezone } from '@core/time-legacy';
import type { SelectOption } from '@core/user-interface-legacy';
import {
  IconComponent,
  ButtonComponent,
  SelectComponent,
  FormErrorDirective,
  InputComponent,
  LabelComponent,
  FormFieldComponent,
  DividerComponent,
  RadioComponent,
  DropdownComponent,
} from '@core/user-interface-legacy';

import { DatePickerComponent } from '../date-picker/date-picker.component';
import { TimeZonePickerComponent } from '../time-zone-picker/time-zone-picker.component';

@Component({
  selector: 'rt-due-date-picker',
  templateUrl: 'due-date-picker.component.html',
  styleUrls: ['./due-date-picker.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DropdownComponent,
    ReactiveFormsModule,
    RadioComponent,
    DividerComponent,
    FormFieldComponent,
    LabelComponent,
    InputComponent,
    FormErrorDirective,
    SelectComponent,
    DatePickerComponent,
    TimeZonePickerComponent,
    ButtonComponent,
    IconComponent,
    TranslatePipe,
  ],
})
export class DueDatePickerComponent {
  private readonly i18n = inject(I18nService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly injector = inject(Injector);

  public readonly errors: { [key: string]: string } = {};
  public readonly units: SelectOption[] = [
    { value: 'days', label: this.i18n.translate('app.automated_actions.action_editor.units.days') },
    {
      value: 'weeks',
      label: this.i18n.translate('app.automated_actions.action_editor.units.weeks'),
    },
    {
      value: 'months',
      label: this.i18n.translate('app.automated_actions.action_editor.units.months'),
    },
  ];

  public timezone = input<string>(getDefaultTimezone());
  public dueDateType = input.required<'relative' | 'absolute'>();
  public dueDateUnit = input.required<string>();
  public dueDateValue = input.required<string>();
  public showTimezone = input<boolean>(true);
  public relativeLabel = input<string>(
    this.i18n.translate('app.automated_actions.action_editor.due_date_in'),
  );
  public submitLabel = input<string>(this.i18n.translate('app.common.done'));

  public valueChanges = output<AutomatedActionDueDate>();
  public typeChange = output<'relative' | 'absolute'>();

  public form = this.fb.group({
    dueDateType: ['relative'],
    dueDateUnit: ['days'],
    dueDateValue: ['10', Validators.compose([Validators.required, Validators.pattern(/^\d+$/)])],
    timezone: getDefaultTimezone(),
    dueDateDate: [this.getUtcMidday()],
  });

  public ngOnInit(): void {
    if (this.dueDateType()) {
      this.form.get('dueDateType')!.setValue(this.dueDateType());
    }
    if (this.dueDateUnit()) {
      this.form.get('dueDateUnit')!.setValue(this.dueDateUnit());
    }
    if (this.dueDateValue()) {
      this.form.get('dueDateValue')!.setValue(this.dueDateValue());
    }
    if (this.timezone()) {
      this.form.get('timezone')!.setValue(this.timezone());
    }

    if (this.dueDateType() === 'relative') {
      this.form.get('dueDateValue')!.setValue(this.dueDateValue());
    } else {
      this.form.get('dueDateDate')!.setValue(this.getUtcMidday(this.dueDateValue()));
    }
  }

  public onKindChange(kind: 'absolute' | 'relative'): void {
    this.form.get('dueDateType')!.setValue(kind);
    afterNextRender(() => this.typeChange.emit(kind), { injector: this.injector });
  }

  public onZoneChange(timezone: string): void {
    this.form.get('timezone')!.setValue(timezone);
  }

  public onDateChange(date: string): void {
    this.form.get('dueDateDate')!.setValue(date);
  }

  public isValid(): boolean {
    if (this.form.get('dueDateType')!.value === 'relative') {
      return this.form.get('dueDateValue')!.valid;
    }
    const date = this.form.get('dueDateDate')!.value;
    return !!date && moment.utc(date).isValid();
  }

  public submit(): void {
    if (!this.isValid()) {
      return;
    }
    const { dueDateType, dueDateUnit, dueDateValue, timezone, dueDateDate } = this.form.value;

    this.valueChanges.emit(
      dueDateType === 'relative'
        ? { dueDateType, timezone, dueDateUnit, dueDateValue }
        : {
            dueDateType,
            timezone,
            dueDateValue: this.getUtcMidday(dueDateDate),
          },
    );
  }

  private getUtcMidday(date: string | undefined = undefined): string {
    return moment.utc(date).set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).toISOString();
  }
}
